import type { IFormField, ITableHeaders, LegendItem } from '../../types';

export const styleConstants = {
  extraSmallHeaderHeight: '89px',
  headerHeight: '105px',
  footerHeight: '30px',
};

export const popperModifier = [
  {
    name: 'flip',
    enabled: false,
    options: {
      altBoundary: false,
      rootBoundary: 'viewport',
    },
  },
  {
    name: 'preventOverflow',
    enabled: true,
    options: {
      altAxis: true,
      altBoundary: true,
      tether: true,
      rootBoundary: 'document',
    },
  },
];

export const IFrameTitle = 'Configuration Application IFrame';

export enum ECellType {
  Link = 'Link',
  Checkbox = 'Checkbox',
  Boolean = 'Boolean',
  Channels = 'Channels',
  Chips = 'Chips',
  Images = 'Images',
  LongSalesText = 'LongSalesText',
  ShortSalesText = 'ShortSalesText',
  Price = 'Price',
  ArrayChips = 'ArrayChips',
  ObjectChips = 'ObjectChips',
  Date = 'Date',
  Update = 'Update',
  Details = 'Details',
  MaxDate = 'MaxDate',
  Text = 'Text',
  Tooltip = 'Tooltip',
  Views = 'Views',
  Log = 'Log',
  SyncStatus = 'SyncStatus',
  Status = 'Status',
  Refresh = 'Refresh',
  Object = 'Object',
  Error = 'Error',
  LinearProgress = 'LinearProgress',
  Download = 'Download',
}

export enum ECellSubType {
  Roles = 'Roles',
  Channels = 'Channels',
  AssociatedMarkets = 'AssociatedMarkets',
  AssociatedLanguages = 'AssociatedLanguages',
  Languages = 'Languages',
  Countires = 'Countires',
  Images = 'Images',
  Views = 'Views',
  Applicability = 'Applicability',
}

export enum EFileSizeMB {
  MB5 = 5,
  MB10 = 10,
  MB20 = 20,
}

export enum EFileSizeBytes {
  KB1500 = 1500000,
  KB50 = 50000,
  KB2000 = 2000000,
}

export enum EImportStatus {
  Success = 'success',
  Error = 'error',
  InvalidType = 'invalidType',
  InvalidSize = 'invalidSize',
  InvalidFileName = 'invalidFileName',
  InvalidZipFileName = 'invalidZipFileName',
  ConnectionError = 'ConnectionError',
}

export const RecordLength = [15, 25, 50, 100];

export const FillFieldsWithValueAsNo = ['isActive', 'active'];

export enum ECompareStatusCellValues {
  //CompareStatus PH Cell Values
  Status = 'compareStatus',
  Added = 'Added',
  Updated = 'Updated',
  Deleted = 'Deleted',
  ModifiedCells = 'modifiedCells',
}

export enum EActivity {
  Publish = 'Publish', //Publish PH
  Discard = 'Discard', //Discards PH
}

export const ComparePHLegend: LegendItem[] = [
  { label: 'Added', color: 'lightgreen', shape: 'square' },
  { label: 'Updated', color: 'yellow', shape: 'square' },
  { label: 'Deleted', color: 'pink', shape: 'square' },
];

export const TableHeaders: Record<string, { main: ITableHeaders[] }> = {
  users: {
    main: [
      {
        field: 'name',
        width: 200,
        sort: true,
        searchable: true,
        type: ECellType.Link,
      },
      {
        field: 'emailId',
        width: 300,
        sort: true,
        searchable: true,
      },
      {
        field: 'isActive',
        width: 100,
        sort: true,
        type: ECellType.Boolean,
      },
      {
        field: 'isAdmin',
        width: 100,
        sort: true,
        type: ECellType.Boolean,
      },
      {
        field: 'isService',
        width: 100,
        sort: true,
        type: ECellType.Boolean,
      },
      {
        field: 'isAce',
        width: 100,
        sort: true,
        type: ECellType.Boolean,
      },
      {
        field: 'isPdm',
        width: 100,
        sort: true,
        type: ECellType.Boolean,
      },
      {
        field: 'roles',
        width: 500,
        sort: true,
        type: ECellType.ObjectChips,
        subType: ECellSubType.Roles,
        detailedViewHeader: 'roles',
        hasIcon: true,
      },
      {
        field: 'createdBy',
        width: 200,
        sort: true,
      },
      {
        field: 'modifiedBy',
        width: 200,
        sort: true,
      },
      {
        field: 'createdOn',
        width: 180,
        sort: true,
        type: ECellType.Date,
      },
      {
        field: 'modifiedOn',
        width: 180,
        sort: true,
        type: ECellType.Date,
      },
      {
        field: 'lastLogin',
        width: 180,
        sort: true,
        type: ECellType.Date,
      },
    ],
  },
  roleAssociationTab: {
    main: [
      {
        field: 'name',
      },
      {
        field: 'description',
      },
    ],
  },
  roles: {
    main: [
      {
        field: 'name',
        width: 250,
        sort: true,
        searchable: true,
        type: ECellType.Link,
      },
      {
        field: 'description',
        width: 250,
        sort: true,
        searchable: true,
      },
      {
        field: 'isActive',
        width: 100,
        sort: true,
        type: ECellType.Boolean,
      },
      {
        field: 'isDefault',
        width: 100,
        sort: true,
        type: ECellType.Boolean,
      },
      {
        field: 'createdBy',
        width: 200,
        sort: true,
      },
      {
        field: 'modifiedBy',
        width: 200,
        sort: true,
      },
      {
        field: 'createdOn',
        width: 180,
        sort: true,
        type: ECellType.Date,
      },
      {
        field: 'modifiedOn',
        width: 180,
        sort: true,
        type: ECellType.Date,
      },
    ],
  },
  apikeys: {
    main: [
      {
        field: 'key',
        width: 200,
        searchable: true,
        type: ECellType.Link,
      },
      {
        field: 'name',
        width: 200,
        sort: true,
        searchable: true,
      },
      {
        field: 'description',
        width: 250,
        sort: true,
        searchable: true,
      },
      {
        field: 'isActive',
        width: 100,
        sort: true,
        type: ECellType.Boolean,
      },
      {
        field: 'application',
        width: 200,
        sort: true,
      },
      {
        field: 'userEmail',
        width: 300,
        sort: true,
      },
      {
        field: 'createdBy',
        width: 200,
        sort: true,
      },
      {
        field: 'modifiedBy',
        width: 200,
        sort: true,
      },
      {
        field: 'createdOn',
        width: 180,
        sort: true,
        type: ECellType.Date,
      },
      {
        field: 'modifiedOn',
        width: 180,
        sort: true,
        type: ECellType.Date,
      },
    ],
  },
  applications: {
    main: [
      {
        field: 'name',
        width: 150,
        searchable: true,
        sort: true,
        type: ECellType.Link,
      },
      {
        field: 'description',
        width: 250,
        searchable: true,
        sort: true,
      },
      {
        field: 'channel',
        width: 150,
        sort: true,
      },
      {
        field: 'views',
        width: 150,
        sort: true,
        type: ECellType.ArrayChips,
        subType: ECellSubType.Views,
        detailedViewHeader: 'views',
        hasIcon: true,
      },
      {
        field: 'isActive',
        width: 100,
        sort: true,
        type: ECellType.Boolean,
      },
      {
        field: 'createdBy',
        width: 200,
        sort: true,
      },
      {
        field: 'modifiedBy',
        width: 200,
        sort: true,
      },
      {
        field: 'createdOn',
        width: 180,
        sort: true,
        type: ECellType.Date,
      },
      {
        field: 'modifiedOn',
        width: 180,
        sort: true,
        type: ECellType.Date,
      },
    ],
  },
  salesText: {
    main: [
      {
        field: 'materialCode',
        width: 150,
        sort: true,
        searchable: true,
      },
      {
        field: 'countryCode',
        width: 150,
        sort: true,
      },
      {
        field: 'languageCode',
        width: 150,
        sort: true,
      },
      {
        field: 'shortSalesText',
        width: 350,
        sort: true,
      },
      {
        field: 'longSalesText',
        width: 100,
        filter: false,
        type: ECellType.LongSalesText,
      },
      {
        field: 'createdBy',
        width: 200,
        sort: true,
      },
      {
        field: 'modifiedBy',
        width: 200,
        sort: true,
      },
      {
        field: 'createdOn',
        width: 180,
        sort: true,
        type: ECellType.Date,
      },
      {
        field: 'modifiedOn',
        width: 180,
        sort: true,
        type: ECellType.Date,
      },
    ],
  },
  images: {
    main: [
      {
        field: 'materialCode',
        width: 200,
        sort: true,
        searchable: true,
      },
      {
        field: 'materialImages',
        width: 580,
        sort: true,
        filter: true,
        type: ECellType.Images,
      },
      {
        field: 'details',
        width: 60,
        type: ECellType.Details,
        subType: ECellSubType.Images,
        detailedViewHeader: 'images',
      },
      {
        field: 'lastUpdatedOn',
        width: 180,
        sort: true,
        type: ECellType.MaxDate,
        key: 'materialImages',
      },
    ],
    details: [
      {
        field: 'image',
        width: '30%',
        cellType: ECellType.Link,
        key: 'url',
      },
      {
        field: 'createdBy',
        width: '15%',
      },
      {
        field: 'createdOn',
        width: '20%',
        cellType: ECellType.Date,
      },
      {
        field: 'modifiedBy',
        width: '15%',
      },
      {
        field: 'modifiedOn',
        width: '20%',
        cellType: ECellType.Date,
      },
    ],
  },
  productHierarchy: {
    main: [
      {
        field: 'sc',
        width: 125,
        sort: true,
      },
      {
        field: 'pd',
        width: 125,
        sort: true,
      },
      {
        field: 'bs',
        width: 125,
        sort: true,
      },
      {
        field: 'bu',
        width: 125,
        sort: true,
        searchable: true,
      },
      {
        field: 'mag',
        width: 125,
        sort: true,
      },
      {
        field: 'ag',
        width: 125,
        sort: true,
        searchable: true,
      },
      {
        field: 'description',
        width: 350,
        sort: true,
      },
      {
        field: 'createdBy',
        width: 200,
        sort: true,
      },
      {
        field: 'modifiedBy',
        width: 200,
        sort: true,
      },
      {
        field: 'createdOn',
        width: 180,
        sort: true,
        type: ECellType.Date,
      },
      {
        field: 'modifiedOn',
        width: 180,
        sort: true,
        type: ECellType.Date,
      },
    ],
  },
  compareProductHierarchy: {
    main: [
      {
        field: 'sc',
        width: 100,
        sort: true,
        index: 1,
        type: ECellType.Update,
      },
      {
        field: 'pd',
        width: 100,
        sort: true,
        index: 2,
        type: ECellType.Update,
      },
      {
        field: 'bs',
        width: 100,
        sort: true,
        index: 3,
        type: ECellType.Update,
      },
      {
        field: 'bu',
        width: 100,
        sort: true,
        searchable: true,
        index: 4,
        type: ECellType.Update,
      },
      {
        field: 'mag',
        width: 100,
        sort: true,
        index: 5,
        type: ECellType.Update,
      },
      {
        field: 'ag',
        width: 100,
        sort: true,
        searchable: true,
        index: 6,
        type: ECellType.Update,
      },
      {
        field: 'description',
        width: 300,
        sort: true,
        index: 7,
        type: ECellType.Update,
      },
    ],
  },
  prices: {
    main: [
      {
        field: 'materialCode',
        width: 250,
        flex: 3,
        sort: true,
        searchable: true,
      },
      {
        field: 'countryCode',
        width: 250,
        flex: 3,
        sort: true,
      },
      {
        field: 'currencyCode',
        width: 250,
        flex: 3,
        sort: true,
      },
      {
        field: 'price',
        width: 250,
        flex: 3,
        sort: true,
        type: ECellType.Price,
      },
      {
        field: 'createdBy',
        width: 200,
        sort: true,
      },
      {
        field: 'modifiedBy',
        width: 200,
        sort: true,
      },
      {
        field: 'createdOn',
        width: 180,
        sort: true,
        type: ECellType.Date,
      },
      {
        field: 'modifiedOn',
        width: 180,
        sort: true,
        type: ECellType.Date,
      },
    ],
  },
  priceListMap: {
    main: [
      {
        field: 'country',
        width: 150,
        sort: true,
        searchable: true,
        type: ECellType.Link,
      },
      {
        field: 'map',
        width: 150,
        sort: true,
      },
      {
        field: 'createdBy',
        width: 200,
        sort: true,
      },
      {
        field: 'modifiedBy',
        width: 200,
        sort: true,
      },
      {
        field: 'createdOn',
        width: 180,
        sort: true,
        type: ECellType.Date,
      },
      {
        field: 'modifiedOn',
        width: 180,
        sort: true,
        type: ECellType.Date,
      },
    ],
  },
  markets: {
    main: [
      {
        field: 'code',
        searchable: true,
        width: 250,
        sort: true,
        type: ECellType.Link,
      },
      {
        field: 'name',
        searchable: true,
        width: 250,
        sort: true,
      },
      {
        field: 'description',
        searchable: true,
        width: 250,
        sort: true,
      },
      {
        field: 'isActive',
        width: 100,
        sort: true,
        type: ECellType.Boolean,
      },
      {
        field: 'createdBy',
        width: 200,
        sort: true,
      },
      {
        field: 'modifiedBy',
        width: 200,
        sort: true,
      },
      {
        field: 'createdOn',
        width: 180,
        sort: true,
        type: ECellType.Date,
      },
      {
        field: 'modifiedOn',
        width: 180,
        sort: true,
        type: ECellType.Date,
      },
    ],
  },
  marketAssociation: {
    main: [
      {
        field: 'productCode',
        width: 200,
        searchable: true,
        sort: true,
        type: ECellType.Link,
      },
      {
        field: 'markets',
        width: 580,
        sort: true,
        type: ECellType.ObjectChips,
        hasIcon: false,
      },
      {
        field: 'details',
        width: 60,
        type: ECellType.Details,
        subType: ECellSubType.AssociatedMarkets,
        detailedViewHeader: 'associatedMarkets',
      },
      {
        field: 'lastUpdatedOn',
        width: 180,
        sort: true,
        type: ECellType.MaxDate,
        key: 'markets',
      },
      {
        field: 'packageType',
        width: 150,
        sort: true,
      },
    ],
    details: [
      {
        field: 'market',
        width: '20%',
        cellType: ECellType.Text,
        key: 'code',
      },
      {
        field: 'createdBy',
        width: '20%',
      },
      {
        field: 'createdOn',
        width: '20%',
        cellType: ECellType.Date,
      },
      {
        field: 'modifiedBy',
        width: '20%',
      },
      {
        field: 'modifiedOn',
        width: '20%',
        cellType: ECellType.Date,
      },
    ],
  },
  languages: {
    main: [
      {
        field: 'code',
        width: 250,
        sort: true,
        searchable: true,
        type: ECellType.Link,
      },
      {
        field: 'name',
        width: 250,
        sort: true,
        searchable: true,
      },
      {
        field: 'description',
        width: 250,
        sort: true,
        searchable: true,
      },
      {
        field: 'isActive',
        width: 100,
        sort: true,
        type: ECellType.Boolean,
      },
      {
        field: 'createdBy',
        width: 200,
        sort: true,
      },
      {
        field: 'modifiedBy',
        width: 200,
        sort: true,
      },
      {
        field: 'createdOn',
        width: 180,
        sort: true,
        type: ECellType.Date,
      },
      {
        field: 'modifiedOn',
        width: 180,
        sort: true,
        type: ECellType.Date,
      },
    ],
  },
  languageAssociation: {
    main: [
      {
        field: 'productCode',
        width: 200,
        sort: true,
        searchable: true,
        type: ECellType.Link,
      },
      {
        field: 'languages',
        width: 580,
        sort: true,
        type: ECellType.ObjectChips,
        hasIcon: false,
      },
      {
        field: 'details',
        width: 60,
        type: ECellType.Details,
        subType: ECellSubType.AssociatedLanguages,
        detailedViewHeader: 'associatedLanguages',
      },
      {
        field: 'lastUpdatedOn',
        width: 180,
        sort: true,
        type: ECellType.MaxDate,
        key: 'languages',
      },
      {
        field: 'packageType',
        width: 150,
        sort: true,
      },
    ],
    details: [
      {
        field: 'language',
        width: '20%',
        cellType: ECellType.Text,
        key: 'code',
      },
      {
        field: 'createdBy',
        width: '20%',
      },
      {
        field: 'createdOn',
        width: '20%',
        cellType: ECellType.Date,
      },
      {
        field: 'modifiedBy',
        width: '20%',
      },
      {
        field: 'modifiedOn',
        width: '20%',
        cellType: ECellType.Date,
      },
    ],
  },
  productCatalog: {
    main: [
      {
        field: 'productId',
        width: 150,
        sort: true,
        searchable: true,
      },
      {
        field: 'productDescription',
        width: 200,
        sort: true,
        searchable: true,
      },
      {
        field: 'agCode',
        width: 150,
        sort: true,
      },
      {
        field: 'packageType',
        width: 125,
        sort: true,
      },
      {
        field: 'channels',
        width: 150,
        sort: true,
        type: ECellType.ArrayChips,
        subType: ECellSubType.Channels,
        detailedViewHeader: 'channels',
        hasIcon: true,
      },
      {
        field: 'isActive',
        width: 100,
        sort: true,
        type: ECellType.Boolean,
      },
      {
        field: 'isHidden',
        width: 100,
        sort: true,
        type: ECellType.Boolean,
      },
      {
        field: 'languages',
        width: 300,
        sort: true,
        type: ECellType.ObjectChips,
        subType: ECellSubType.Languages,
        detailedViewHeader: 'languages',
        hasIcon: true,
      },
      {
        field: 'countries',
        width: 700,
        sort: true,
        type: ECellType.ObjectChips,
        subType: ECellSubType.Countires,
        detailedViewHeader: 'countries',
        hasIcon: true,
      },
      {
        field: 'createdBy',
        width: 200,
        sort: true,
      },
      {
        field: 'modifiedBy',
        width: 200,
        sort: true,
      },
      {
        field: 'createdOn',
        width: 180,
        sort: true,
        type: ECellType.Date,
      },
      {
        field: 'modifiedOn',
        width: 180,
        sort: true,
        type: ECellType.Date,
      },
    ],
  },
  internalProductCatalog: {
    main: [
      {
        field: 'productId',
        width: 150,
        sort: true,
        searchable: true,
      },
      {
        field: 'productDescription',
        width: 200,
        sort: true,
        searchable: true,
      },
      {
        field: 'agCode',
        width: 150,
        sort: true,
      },
      {
        field: 'packageType',
        width: 125,
        sort: true,
      },
      {
        field: 'channels',
        width: 150,
        sort: true,
        type: ECellType.ArrayChips,
        subType: ECellSubType.Channels,
        detailedViewHeader: 'channels',
        hasIcon: true,
      },
      {
        field: 'isActive',
        width: 100,
        sort: true,
        type: ECellType.Boolean,
      },
      {
        field: 'isHidden',
        width: 100,
        sort: true,
        type: ECellType.Boolean,
      },
      {
        field: 'languages',
        width: 300,
        sort: true,
        type: ECellType.ObjectChips,
        subType: ECellSubType.Languages,
        detailedViewHeader: 'languages',
        hasIcon: true,
      },
      {
        field: 'countries',
        width: 700,
        sort: true,
        type: ECellType.ObjectChips,
        subType: ECellSubType.Countires,
        detailedViewHeader: 'countries',
        hasIcon: true,
      },
      {
        field: 'createdBy',
        width: 200,
        sort: true,
      },
      {
        field: 'modifiedBy',
        width: 200,
        sort: true,
      },
      {
        field: 'createdOn',
        width: 180,
        sort: true,
        type: ECellType.Date,
      },
      {
        field: 'modifiedOn',
        width: 180,
        sort: true,
        type: ECellType.Date,
      },
    ],
  },
  productConfiguration: {
    main: [
      {
        field: 'configurationId',
        width: 275,
        searchable: true,
        sort: true,
      },
      {
        field: 'name',
        width: 250,
        sort: true,
      },
      {
        field: 'state',
        width: 100,
        sort: true,
      },
      {
        field: 'productId',
        width: 100,
        searchable: true,
        sort: true,
      },
      {
        field: 'productDescription',
        width: 150,
        searchable: true,
        sort: true,
      },
      {
        field: 'countryCode',
        width: 100,
        sort: true,
      },
      {
        field: 'countryName',
        width: 200,
        sort: true,
      },
      {
        field: 'packagePath',
        width: 200,
        sort: true,
      },

      {
        field: 'createdFromName',
        width: 375,
        sort: true,
      },
      {
        field: 'createdFromId',
        width: 275,
        sort: true,
      },
      {
        field: 'createdBy',
        width: 200,
        sort: true,
      },
      {
        field: 'modifiedBy',
        width: 200,
        sort: true,
      },
      {
        field: 'createdOn',
        width: 200,
        sort: true,
        type: ECellType.Date,
      },
      {
        field: 'modifiedOn',
        width: 200,
        sort: true,
        type: ECellType.Date,
      },
    ],
  },
  archivedConfiguration: {
    main: [
      {
        field: 'configurationId',
        width: 275,
        searchable: true,
        sort: true,
      },
      {
        field: 'name',
        width: 250,
        sort: true,
      },
      {
        field: 'state',
        width: 100,
        sort: true,
      },
      {
        field: 'productId',
        width: 100,
        searchable: true,
        sort: true,
      },
      {
        field: 'productDescription',
        width: 150,
        searchable: true,
        sort: true,
      },
      {
        field: 'countryCode',
        width: 100,
        sort: true,
      },
      {
        field: 'countryName',
        width: 200,
        sort: true,
      },
      {
        field: 'packagePath',
        width: 200,
        sort: true,
      },
      {
        field: 'createdFromName',
        width: 375,
        sort: true,
      },
      {
        field: 'createdBy',
        width: 200,
        sort: true,
      },
      {
        field: 'modifiedBy',
        width: 200,
        sort: true,
      },
      {
        field: 'deletedBy',
        width: 200,
        searchable: true,
        sort: true,
      },
      {
        field: 'createdOn',
        width: 200,
        sort: true,
        type: ECellType.Date,
      },
      {
        field: 'modifiedOn',
        width: 200,
        sort: true,
        type: ECellType.Date,
      },
      {
        field: 'deletedOn',
        width: 200,
        sort: true,
        type: ECellType.Date,
      },
    ],
  },
  featureFlags: {
    main: [
      {
        field: 'flagName',
        width: 150,
        sort: true,
        searchable: true,
        type: ECellType.Link,
      },
      {
        field: 'description',
        width: 250,
        searchable: true,
        sort: true,
      },
      {
        field: 'applicationName',
        width: 150,
        sort: true,
      },
      {
        field: 'page',
        width: 150,
        sort: true,
      },
      {
        field: 'isActive',
        width: 100,
        sort: true,
        type: ECellType.Boolean,
      },
      {
        field: 'isVisible',
        width: 100,
        sort: true,
        type: ECellType.Boolean,
      },
      {
        field: 'isEditable',
        width: 100,
        sort: true,
        type: ECellType.Boolean,
      },
      {
        field: 'createdBy',
        width: 200,
        sort: true,
      },
      {
        field: 'modifiedBy',
        width: 200,
        sort: true,
      },
      {
        field: 'createdOn',
        width: 180,
        sort: true,
        type: ECellType.Date,
      },
      {
        field: 'modifiedOn',
        width: 180,
        sort: true,
        type: ECellType.Date,
      },
    ],
  },
  ace: {
    main: [
      {
        field: 'code',
        width: 270,
        sort: true,
        searchable: true,
        type: ECellType.Link,
      },
      {
        field: 'refresh',
        filter: false,
        width: 65,
        type: ECellType.Refresh,
        align: 'center',
      },
      {
        field: 'targetId',
        width: 200,
        sort: true,
        searchable: true,
      },
      {
        field: 'type',
        width: 100,
        sort: true,
        searchable: true,
      },
      {
        field: 'action',
        width: 100,
        sort: true,
        searchable: true,
      },
      {
        field: 'isActive',
        width: 80,
        searchable: true,
        type: ECellType.Boolean,
        align: 'center',
      },
      {
        field: 'status',
        width: 80,
        searchable: true,
        type: ECellType.Status,
        align: 'center',
        rowHighlighter: true,
      },
      {
        field: 'processedOn',
        width: 180,
        sort: true,
        type: ECellType.Date,
      },
      {
        field: 'log',
        width: 60,
        filter: false,
        type: ECellType.Log,
        sort: true,
        align: 'center',
      },
      {
        field: 'sync',
        width: 70,
        filter: false,
        type: ECellType.SyncStatus,
        sort: true,
        align: 'center',
      },
      {
        field: 'createdOn',
        width: 180,
        sort: true,
        type: ECellType.Date,
      },
      {
        field: 'modifiedOn',
        width: 180,
        sort: true,
        type: ECellType.Date,
      },
      {
        field: 'createdBy',
        width: 200,
        sort: true,
      },
      {
        field: 'modifiedBy',
        width: 200,
        sort: true,
      },
      {
        field: 'retrigger',
        width: 100,
        filter: false,
        type: ECellType.Chips,
        align: 'center',
      },
    ],
  },
  queue: {
    main: [
      {
        field: 'code',
        type: ECellType.Link,
        sort: true,
        filter: true,
        searchable: true,
        width: 150,
      },
      {
        field: 'source',
        type: ECellType.Text,
        sort: true,
        width: 150,
      },
      {
        field: 'type',
        sort: true,
        width: 150,
      },
      {
        field: 'txnId',
        sort: true,
        filter: true,
        searchable: true,
        width: 150,
      },
      {
        field: 'refresh',
        type: ECellType.Refresh, // Shows refresh icon
        sort: false,
        width: 80,
      },
      {
        field: 'isActive',
        type: ECellType.Boolean,
        sort: true,
        filter: true,
        searchable: true,
        width: 80,
      },
      {
        field: 'status',
        type: ECellType.Status, // Shows status icon like CheckCircle, Cancel
        sort: true,
        filter: true,
        searchable: true,
        width: 80,
      },
      {
        field: 'log',
        type: ECellType.Log, // Shows log icon with dialog
        sort: false,
        width: 50,
      },
      {
        field: 'error',
        width: 50,
        filter: false,
        type: ECellType.Error,
        sort: true,
        align: 'center',
      },
      {
        field: 'user',
        sort: true,
        width: 200,
      },
      {
        field: 'createdOn',
        type: ECellType.Date,
        sort: true,
        width: 150,
      },
      {
        field: 'processedOn',
        type: ECellType.Date,
        sort: true,
        width: 150,
      },
      {
        field: 'syncedOn',
        type: ECellType.Date,
        sort: true,
        width: 150,
      },
    ],
  },
  properties: {
    main: [
      {
        field: 'code',
        width: 150,
        sort: true,
        searchable: true,
        type: ECellType.Link,
      },
      {
        field: 'name',
        width: 150,
        sort: true,
        searchable: true,
      },
      {
        field: 'type',
        width: 100,
        sort: true,
      },
      {
        field: 'description',
        width: 200,
        sort: true,
        type: ECellType.Tooltip,
      },
      {
        field: 'values',
        width: 150,
        sort: true,
        type: ECellType.Text,
      },
      {
        field: 'applicability',
        width: 170,
        sort: true,
        type: ECellType.ArrayChips,
        subType: ECellSubType.Applicability,
        detailedViewHeader: 'applicability',
        hasIcon: true,
      },
      {
        field: 'isRequired',
        width: 100,
        sort: true,
        type: ECellType.Boolean,
      },
      {
        field: 'default',
        width: 100,
        sort: true,
        type: ECellType.Object,
      },
      {
        field: 'origin',
        width: 100,
        sort: true,
      },
      {
        field: 'isActive',
        width: 100,
        sort: true,
        type: ECellType.Boolean,
      },
      {
        field: 'createdBy',
        width: 200,
        sort: true,
      },
      {
        field: 'modifiedBy',
        width: 200,
        sort: true,
      },
      {
        field: 'createdOn',
        width: 180,
        sort: true,
        type: ECellType.Date,
      },
      {
        field: 'modifiedOn',
        width: 180,
        sort: true,
        type: ECellType.Date,
      },
    ],
  },
  lov: {
    main: [
      {
        field: 'code',
        width: 100,
        sort: true,
        searchable: true,
        type: ECellType.Link, // opens LovOptionsManagement dialog via onCellClick
      },
      { field: 'name', width: 150, sort: true, searchable: true, isEditable: true },
      {
        field: 'isActive',
        width: 100,
        sort: true,
        isEditable: true,
        type: ECellType.Boolean,
      },
      { field: 'createdBy', width: 160, sort: true },
      { field: 'createdOn', width: 180, sort: true, type: ECellType.Date },
      { field: 'modifiedBy', width: 160, sort: true },
      { field: 'modifiedOn', width: 180, sort: true, type: ECellType.Date },
    ],
  },
  lovOptions: {
    main: [
      { field: 'code', width: 100, sort: true, searchable: true },
      { field: 'name', width: 150, sort: true, searchable: true, isEditable: true },
      {
        field: 'isActive',
        width: 100,
        sort: true,
        isEditable: true,
        type: ECellType.Boolean,
      },
      { field: 'createdBy', width: 160, sort: true },
      { field: 'createdOn', width: 180, sort: true, type: ECellType.Date },
      { field: 'modifiedBy', width: 160, sort: true },
      { field: 'modifiedOn', width: 180, sort: true, type: ECellType.Date },
    ],
  },
  propertyAssociation: {
    main: [
      {
        field: 'productCode',
        width: 200,
        sort: true,
        searchable: true,
        type: ECellType.Link,
      },
      {
        field: 'description',
        width: 200,
        sort: true,
        searchable: true,
      },
    ],
  },
  featurePropertyAssociation: {
    main: [
      {
        field: 'code',
        width: 150,
        sort: true,
        searchable: true,
      },
      {
        field: 'description',
        width: 250,
        sort: true,
        searchable: true,
      },
    ],
  },
  multiNumericFeature: {
    main: [
      {
        field: 'code',
        width: 150,
        sort: true,
        searchable: true,
        type: ECellType.Link,
      },
      {
        field: 'name',
        width: 150,
        sort: true,
        searchable: true,
      },
      {
        field: 'isLocal',
        width: 100,
        sort: true,
        type: ECellType.Boolean,
      },
      {
        field: 'isActive',
        width: 100,
        sort: true,
        type: ECellType.Boolean,
      },
      {
        field: 'createdBy',
        width: 200,
        sort: true,
      },
      {
        field: 'modifiedBy',
        width: 200,
        sort: true,
      },
      {
        field: 'createdOn',
        width: 180,
        sort: true,
        type: ECellType.Date,
      },
      {
        field: 'modifiedOn',
        width: 180,
        sort: true,
        type: ECellType.Date,
      },
    ],
  },
  productRelease: {
    main: [
      {
        field: 'id',
        sort: true,
        width: 150,
      },
      {
        field: 'productCode',
        sort: true,
        width: 150,
        searchable: true,
      },
      {
        field: 'refresh',
        filter: false,
        width: 65,
        type: ECellType.Refresh,
        align: 'center',
        sort: false,
      },
      {
        field: 'version',
        sort: true,
        width: 250,
        searchable: true,
      },
      {
        field: 'packageType',
        sort: true,
        width: 125,
      },
      {
        field: 'status',
        width: 80,
        searchable: true,
        type: ECellType.Status,
        align: 'center',
        rowHighlighter: true,
      },
      {
        field: 'log',
        width: 60,
        filter: false,
        type: ECellType.Log,
        sort: false,
        align: 'center',
      },
      {
        field: 'activities',
        sort: false,
        width: 150,
        type: ECellType.LinearProgress,
      },

      {
        field: 'processedOn',
        width: 150,
        sort: true,
      },
      {
        field: 'createdOn',
        width: 150,
        sort: true,
      },
      {
        field: 'createdBy',
        width: 80,
        sort: true,
      },
      {
        field: 'remarks',
        width: 150,
        sort: true,
      },
      {
        field: 'retrigger',
        width: 100,
        filter: false,
        type: ECellType.Chips,
        align: 'center',
        sort: false,
      },
      {
        field: 'download',
        width: 80,
        filter: false,
        type: ECellType.Download,
        sort: false,
        align: 'center',
      },
    ],
  },
  productVersionMapping: {
    main: [
      {
        field: 'id',
        sort: true,
        width: 100,
      },
      {
        field: 'productId',
        sort: true,
        width: 150,
        searchable: true,
      },
      {
        field: 'refresh',
        filter: false,
        width: 65,
        type: ECellType.Refresh,
        align: 'center',
        sort: false,
      },
      {
        field: 'versionId',
        sort: true,
        width: 250,
      },
      {
        field: 'releaseId',
        sort: true,
        width: 250,
      },
      {
        field: 'createdOn',
        sort: true,
        width: 150,
      },
      {
        field: 'modifiedOn',
        sort: true,
        width: 150,
      },
    ],
  },
  releaseActivity: {
    main: [
      { field: 'activities', flex: 1, sort: false, filter: false },
      { field: 'status', width: 80, sort: false, filter: false, type: ECellType.Status },
    ],
  },
  Descope_List: {
    main: [
      { field: 'id', width: 100, searchable: true, sort: true },
      { field: 'fromDate', width: 180, sort: true, type: ECellType.Date },
      { field: 'toDate', width: 180, sort: true, type: ECellType.Date },
      {
        field: 'status',
        width: 80,
        sort: true,
        type: ECellType.Status,
        align: 'center',
        rowHighlighter: true,
      },
      { field: 'fileName', width: 200, sort: true },
      { field: 'remarks', width: 250, sort: true },
      { field: 'createdOn', width: 180, sort: true, type: ECellType.Date },
      { field: 'processedOn', width: 180, sort: true, type: ECellType.Date },
      { field: 'createdBy', width: 150, sort: true },
      {
        field: 'download',
        width: 80,
        filter: false,
        type: ECellType.Download,
        sort: false,
        align: 'center',
      },
    ],
  },
  coe: {
    main: [
      { field: 'id', width: 100, searchable: true, sort: true },
      {
        field: 'status',
        width: 80,
        sort: true,
        type: ECellType.Status,
        align: 'center',
        rowHighlighter: true,
      },
      { field: 'fileName', width: 200, sort: true },
      { field: 'remarks', width: 250, sort: true },
      { field: 'createdOn', width: 180, sort: true, type: ECellType.Date },
      { field: 'processedOn', width: 180, sort: true, type: ECellType.Date },
      { field: 'createdBy', width: 150, sort: true },
      {
        field: 'download',
        width: 80,
        filter: false,
        type: ECellType.Download,
        sort: false,
        align: 'center',
      },
    ],
  },
  Procos_PPL: {
    main: [
      { field: 'id', width: 100, searchable: true, sort: true },
      {
        field: 'status',
        width: 80,
        sort: true,
        type: ECellType.Status,
        align: 'center',
        rowHighlighter: true,
      },
      { field: 'fileName', width: 200, sort: true },
      { field: 'remarks', width: 250, sort: true },
      { field: 'createdOn', width: 180, sort: true, type: ECellType.Date },
      { field: 'processedOn', width: 180, sort: true, type: ECellType.Date },
      { field: 'createdBy', width: 150, sort: true },
      {
        field: 'download',
        width: 80,
        filter: false,
        type: ECellType.Download,
        sort: false,
        align: 'center',
      },
    ],
  },
};

export const foreignKeysForTable: Record<string, string> = {
  users: 'emailId',
  roles: 'name',
  apikeys: 'id',
  applications: 'name',
  languageAssociation: 'productCode',
  productHierarchy: 'sc',
  prices: 'materialCode',
  images: 'materialCode',
  salesText: 'materialCode',
  languages: 'code',
  priceListMap: 'country',
  productCatalog: 'productId',
  productConfiguration: 'configurationId',
  archivedConfiguration: 'configurationId',
  markets: 'code',
  marketAssociation: 'productCode',
  featureFlags: 'flagName',
  lov: 'code',
  lovOptions: 'code',
  properties: 'code',
  propertyAssociation: 'productCode',
  featurePropertyAssociation: 'code',
  multiNumericFeature: 'code',
  ace: 'code',
  queue: 'code',
  productRelease: 'id',
  productVersionMapping: 'versionId',
  Descope_List: 'id',
  coe: 'id',
  Procos_PPL: 'id',
};

export const initialSortField: Record<string, string> = {
  users: 'emailId',
  roles: 'name',
  apikeys: 'id',
  applications: 'name',
  languageAssociation: 'productCode',
  productHierarchy: 'sc',
  prices: 'materialCode',
  images: 'materialCode',
  salesText: 'materialCode',
  languages: 'code',
  priceListMap: 'country',
  productCatalog: 'productId',
  markets: 'code',
  marketAssociation: 'productCode',
  featureFlags: 'flagName',
  lov: 'code',
  lovOptions: 'code',
  properties: 'code',
  propertyAssociation: 'productCode',
};

export const foreignKeysForInfoIcon = {
  roles: {
    roles: 'name',
  },
  marketAssociation: {
    markets: 'productCode',
  },
  languageAssociation: {
    languages: 'productCode',
  },
  productCatalog: {
    languages: 'productId',
    countries: 'productId',
  },
  applications: {
    views: 'views',
  },
};

export enum EActionType {
  Edit = 'Edit',
  Create = 'Create',
  Delete = 'Delete',
  View = 'View',
  Compare = 'Compare',
  Refresh = 'Refresh',
}

export enum EDataMaintenanceType {
  Lov = 'lov',
  LovOptions = 'lovOptions',
  SalesText = 'salesText',
  Images = 'images',
  ProductHierarchy = 'productHierarchy',
  CompareProductHierarchy = 'compareProductHierarchy',
  LanguageAssociation = 'languageAssociation',
  Prices = 'prices',
  Language = 'languages',
  PriceListMap = 'priceListMap',
  ProductCatalog = 'productCatalog',
  InternalProductCatalog = 'internalProductCatalog',
  Market = 'markets',
  MarketAssociation = 'marketAssociation',
  Properties = 'properties',
  PropertyAssociation = 'propertyAssociation',
  FeaturePropertyAssociation = 'featurePropertyAssociation',
  TechnicalInfo = 'technicalInfo',
  Ace = 'ace',
  Queue = 'queue',
  ProductRelease = 'productRelease',
  ProductVersionMapping = 'productVersionMapping',
}

export enum EDataTableType {
  Lov = 'lov',
  LovOptions = 'lovOptions',
  Users = 'users',
  Roles = 'roles',
  ApiKeys = 'apikeys',
  Applications = 'applications',
  Languages = 'languages',
  SalesText = 'salesText',
  Images = 'images',
  ProductHierarchy = 'productHierarchy',
  LanguageAssociation = 'languageAssociation',
  Prices = 'prices',
  PriceListMap = 'priceListMap',
  ProductCatalog = 'productCatalog',
  InternalProductCatalog = 'internalProductCatalog',
  ProductConfiguration = 'productConfiguration',
  ArchivedConfiguration = 'archivedConfiguration',
  Markets = 'markets',
  MarketAssociation = 'marketAssociation',
  FeatureFlags = 'featureFlags',
  Ace = 'ace',
  Queue = 'queue',
  Properties = 'properties',
  PropertyAssociation = 'propertyAssociation',
  FeaturePropertyAssociation = 'featurePropertyAssociation',
  MultiNumericFeature = 'multiNumericFeature',
  TechnicalInfo = 'technicalInfo',
  ProductRelease = 'productRelease',
  ProductVersionMapping = 'productVersionMapping',
  DescopeList = 'Descope_List',
  Coe = 'coe',
  ProcosForPpl = 'Procos_PPL',
}
export const administrationTabs = [
  { id: 1, key: 'User', name: 'User Management', path: '/users' },
  { id: 2, key: 'Role', name: 'Role Management', path: '/roles' },
  { id: 3, key: 'Apikey', name: 'API Key Management', path: '/apikeys' },
  { id: 4, key: 'Application', name: 'Application Management', path: '/applications' },
];

export const dataMaintenanceTabs = [
  { id: 1, key: 'Sales', name: 'Sales Text', path: '/salesTexts' },
  { id: 2, key: 'Image', name: 'Image', path: '/images' },
  { id: 3, key: 'Product', name: 'Product Hierarchy', path: '/productHierarchy' },
  { id: 4, key: 'Price', name: 'Price', path: '/prices' },
  { id: 5, key: 'Pricelist', name: 'Price List Map', path: '/priceListMap' },
  { id: 6, key: 'TechnicalInfo', name: 'Technical Info', path: '/technicalInfo' },
];

export const productTabs = [
  { id: 1, key: 'Market', name: 'Market', path: '/market' },
  { id: 2, key: 'MarketAssociation', name: 'Market Association', path: '/marketAssociation' },
  { id: 3, key: 'Language', name: 'Language', path: '/language' },
  { id: 4, key: 'LanguageAssociation', name: 'Language Association', path: '/languageAssociation' },
  { id: 5, key: 'ProductCatalog', name: 'Product Catalog', path: '/productCatalog' },
  {
    id: 5,
    key: 'InternalProductCatalog',
    name: 'Internal Product Catalog',
    path: '/internalProductCatalog',
  },
  { id: 6, key: 'ProductConfig', name: 'Product Configuration', path: '/productConfiguration' },
  {
    id: 7,
    key: 'Archive',
    name: 'Archived Configuration',
    path: '/archivedConfiguration',
  },
  { id: 8, key: 'Properties', name: 'Properties', path: '/properties' },
  { id: 9, key: 'PropertyAssociation', name: 'Property Association', path: '/propertyAssociation' },
  { id: 10, key: 'ListOfValues', name: 'List of Values', path: '/listOfValues' },
];

export const fearureFlagTabs = [
  { id: 1, key: 'Flag', name: 'Feature Flags', path: '/featureFlags' },
];

export const notificationTabs = [
  { id: 1, key: 'Ace', name: 'Ace', path: '/ace' },
  { id: 2, key: 'Queue', name: 'Queue', path: '/queue' },
  { id: 3, key: 'ProductRelease', name: 'Product Release', path: '/productRelease' },
  {
    id: 4,
    key: 'ProductVersionMapping',
    name: 'Product Version Mapping',
    path: '/productVersionMapping',
  },
];
export const featureTabs = [
  { id: 1, key: 'Feature', name: 'Multi Numeric', path: '/multiNumericFeature' },
];

export const reportTabs = [
  { id: 1, key: 'DescopeList', name: 'Descope List', path: '/descopeList' },
  { id: 2, key: 'Coe', name: 'COE', path: '/coe' },
  { id: 3, key: 'ProcosForPpl', name: 'ProCoS for PPL', path: '/procosForPpl' },
];

export const deleteFields = {
  roles: [
    {
      field: 'isDefault',
      inverseCheck: false,
    },
    {
      field: 'isActive',
      inverseCheck: true,
    },
  ],
  users: {
    field: 'isActive',
    inverseCheck: true,
  },
  apikeys: {
    field: 'isActive',
    inverseCheck: true,
  },
  applications: {
    field: 'isActive',
    inverseCheck: true,
  },
  languages: {
    field: 'isActive',
    inverseCheck: true,
  },
  markets: {
    field: 'isActive',
    inverseCheck: true,
  },
  featureFlags: {
    field: 'isActive',
    inverseCheck: true,
  },
  properties: {
    field: 'isActive',
    inverseCheck: true,
  },
  multiNumericFeature: {
    field: 'isActive',
    inverseCheck: true,
  },
  ace: {
    field: 'isActive',
    inverseCheck: true,
  },
  lov: {
    field: 'isActive',
    inverseCheck: true,
  },
  lovOptions: {
    field: 'isActive',
    inverseCheck: true,
  },
};

export enum ECommonDialogType {
  ApiKeyCreateSuccess = 'apiKeyCreateSuccess',
  EditLanguageAssociation = 'editLanguageAssociation',
  Image = 'image',
  LongSalesText = 'LongSalesText',
  ShortSalesText = 'ShortSalesText',
  Languages = 'languages',
  Markets = 'markets',
  EditMarketAssociation = 'editMarketAssociation',
  EditPriceListMap = 'editPriceListMap',
  Error = 'error',
  Notification = 'notification',
  Ace = 'ace',
  Queue = 'queue',
  Properties = 'properties',
  PropertyAssociation = 'propertyAssociation',
  FeaturePropertyAssociation = 'featurePropertyAssociation',
  DeletePrices = 'deletePrices',
  MultiNumericFeature = 'multiNumericFeature',
  RetriggerProductRelease = 'retriggerProductRelease',
}

export enum ETabValue {
  ShortSalesText = 'ShortSalesText',
  LongSalesText = 'LongSalesText',
  AdministrationTab = 'administration',
}

/** Buttons that can be requested by a parent component via renderButtonSection. */
export enum EToolbarButton {
  Delete = 'Delete',
  Create = 'Create',
  Compare = 'Compare',
}

export enum EBrowserType {
  Chrome = 'Chrome',
  Edge = 'Edge',
  Safari = 'Safari',
}

export enum EModuleType {
  Administration = 'administration',
  Datamaintainance = 'dataMaintainance',
  Lov = 'lovManagement',
  Product = 'product',
  FeatureFlags = 'featureFlags',
  Notifications = 'notifications',
  Feature = 'feature',
  Configuration = 'configuration',
  Reports = 'reports',
}

export enum EAction {
  Read = 'read',
  Create = 'create',
  Update = 'update',
  Get = 'get',
  Retrigger = 'retrigger',
  Download = 'download',
  CreateReport = 'CreateReport',
}

export enum EReportType {
  DescopeList = 'Descope_List',
  Coe = 'COE',
  ProcosForPpl = 'Procos_PPL',
}

export enum EApiType {
  UserKey = 'userKey',
  RoleKey = 'roleKey',
  ApiKey = 'apiKey',
  ApplicationKey = 'applicationKey',
  SalesTextKey = 'salestextKey',
  ImagesKey = 'imagesKey',
  ProductHierarchyKey = 'productHierarchyKey',
  CompareProductHierarchyKey = 'compareProductHierarchyKey',
  PriceKey = 'priceKey',
  PriceListMapKey = 'priceListMapKey',
  MarketKey = 'marketKey',
  MarketAssociationKey = 'marketAssociationKey',
  LanguageKey = 'languageKey',
  LanguageAssociationKey = 'languageAssociationKey',
  ProductCatalogKey = 'productCatalogKey',
  InternalProductCatalogKey = 'internalProductCatalogKey',
  ProductConfigurationKey = 'productConfigurationKey',
  ArchivedConfigurationKey = 'archivedConfigurationKey',
  FeatureFlagsKey = 'featureFlagsKey',
  NotificationKey = 'notificationKey',
  QueueKey = 'queueKey',
  PropertyKey = 'propertyKey',
  PropertyAssociationKey = 'propertyAssociationKey',
  FeaturePropertyAssociationKey = 'featurePropertyAssociationKey',
  MultiNumericFeatureKey = 'multiNumericFeatureKey',
  ProductReleaseKey = 'productReleaseKey',
  ProductVersionMappingKey = 'productVersionMappingKey',
  ReportKey = 'reportKey',
}

export enum EHttpCode {
  Status204 = 204,
  Status400 = 400,
  Status401 = 401,
  Status403 = 403,
  Status404 = 404,
}

export enum EUrlParams {
  ConfigurationId = 'cid',
  ViewId = 'viewId',
  ApiKey = 'cc',
  Model = 'model',
  Token = 'at',
  HeaderLess = 'hl',
  Language = 'l',
  Country = 'c',
  Page = 'Page',
  Limit = 'Limit',
  Search = 'search',
  ProductSearch = 'productSearch',
  Code = 'code',
  CatalogType = 'catalogType',
}

export enum ECatalogType {
  Internal = 'I',
  Release = 'R',
}

export enum EEnvironments {
  DEV = 'DEV',
  PROD = 'PROD',
  TEST = 'TEST',
  ACC = 'ACC',
}

export enum EPageNames {
  UserManagement = '/users',
}

export enum EFileFormat {
  Zip = '.zip',
  Xlsx = '.xlsx',
}

export enum ELocalStore {
  IsUnSupportedBrowserAck = 'isUnsBrowAck',
}

export enum ELovValues {
  ETYP = 'ETYP',
  EAPP = 'EAPP',
  ESCT = 'ESCT',
  ECHT = 'ECHT',
  EVIEW = 'EVIEW',
}

export enum EApplicability {
  Master = 'MSTR',
  NumericMaster = 'NMSTR',
  Product = 'PROD',
}

export const excludeLovValues = ['ETYP', 'EAPP', 'ESCT'];

export enum EPropertyType {
  Bool = 'BOOL',
  Enum = 'ENUM',
  String = 'STR',
  Numeric = 'NUM',
}

export enum EPropertyOrigin {
  Configit = 'Configit',
  Pdm = 'PDM',
}

export enum EStatus {
  Initial = 'I',
  Success = 'S',
  Failed = 'F',
  Processing = 'p',
  New = 'N',
}

export const RegExPatterns = {
  Url: /^(https?|ftp):\/\/\w+(\.\w+)*(:\d+)?\/?(\/[.\w]*)*$/,
  Email: /^[\w\-.]+@([\w-]+\.)+[\w-]{2,}$/,
  AlphaNumeric: /^[A-Za-z0-9]+( [A-Za-z0-9]+)*$/,
  AlphaNumericUnderscore: /^[A-Za-z0-9]+(_[A-Za-z0-9]+)*$/,
  AplhaNumericHyphenUnderscore: /^[A-Za-z0-9]+(([-_])[A-Za-z0-9]+)*$/,
  AlphaNumericSeperators: /^[A-Za-z0-9]+(([-_:])[A-Za-z0-9]+)*$/,
  LanguageCode: /^[a-z]{2}-[A-Z]{2}$/,
  MarketCode: /^[a-zA-Z]{2}$/,
  UserName: /^[A-Za-z0-9\s,]+([\s,]+\([A-Za-z\s]+\))?$/,
};

export const DefaultProperty = [{ code: 'ALL', name: 'ALL', isActive: true }];

export const tabsList = [
  {
    id: EModuleType.Administration,
    childs: [
      EDataTableType.Users,
      EDataTableType.Roles,
      EDataTableType.ApiKeys,
      EDataTableType.Applications,
    ],
  },
  {
    id: EModuleType.Datamaintainance,
    childs: [
      EDataTableType.SalesText,
      EDataTableType.Prices,
      EDataTableType.Images,
      EDataTableType.ProductHierarchy,
      EDataTableType.PriceListMap,
      EDataTableType.TechnicalInfo,
    ],
  },
  {
    id: EModuleType.Product,
    childs: [
      EDataTableType.Markets,
      EDataTableType.MarketAssociation,
      EDataTableType.LanguageAssociation,
      EDataTableType.Languages,
      EDataTableType.ProductCatalog,
      EDataTableType.InternalProductCatalog,
      EDataTableType.ProductConfiguration,
      EDataTableType.ArchivedConfiguration,
      EDataTableType.Properties,
    ],
  },
  {
    id: EModuleType.FeatureFlags,
    childs: [EDataTableType.FeatureFlags],
  },
  {
    id: EModuleType.Feature,
    childs: [EDataTableType.MultiNumericFeature],
  },
  {
    id: EModuleType.Notifications,
    childs: [
      EDataTableType.Ace,
      EDataTableType.Queue,
      EDataTableType.ProductRelease,
      EDataTableType.ProductVersionMapping,
    ],
  },
  {
    id: EModuleType.Reports,
    childs: [EDataTableType.DescopeList, EDataTableType.Coe, EDataTableType.ProcosForPpl],
  },

  {
    id: EModuleType.Configuration,
    childs: [],
  },
];

export const MaxDeleteCount: Record<string, number> = {
  [EDataTableType.MultiNumericFeature]: 10,
  [EDataTableType.Lov]: 5,
  [EDataTableType.LovOptions]: 5,
  Default: 10,
};

export const MaxPageRecordsRetainInState = 5;

export enum EStatusColor {
  Success = 'success',
  Error = 'error',
  Warning = 'warning',
  NoData = 'noData',
}

export enum EFormFieldType {
  Text = 'Text',
  RadioGroup = 'RadioGroup',
  Checkbox = 'Checkbox', //This type is designated for checkboxes with boolean values, such as isDefault: true.
  CheckboxGroup = 'CheckboxGroup', //This type is intended for checkboxes whose values represent an array of objects, such as roles: ['Role1', 'Role2'].
}

export enum EFormCheckboxLabelFormat {
  Name = 'Name',
  Code = 'Code',
  CodeWithName = 'CodeWithName',
}

export const ApplicationFormFields: IFormField[] = [
  {
    field: 'name',
    type: EFormFieldType.Text,
    rules: {
      required: { value: true, message: 'nameErrorMessage' },
      minLength: { value: 3, message: 'minLimitErrorMessage' },
      maxLength: { value: 20, message: 'maxLimitErrorMessage' },
      pattern: { value: RegExPatterns.AplhaNumericHyphenUnderscore, message: 'nameErrorMessage' },
    },
    disableConditions: ['edit'],
  },
  {
    field: 'description',
    type: EFormFieldType.Text,
    rules: {
      required: { value: true, message: 'descriptionErrorMessage' },
      maxLength: { value: 100, message: 'maxLimitErrorMessage' },
    },
    disableConditions: ['isActive'],
  },
  {
    field: 'channel',
    type: EFormFieldType.RadioGroup,
    rules: { required: { value: true, message: 'nameErrorMessage' } },
    disableConditions: ['isActive'],
    values: [],
  },
  {
    field: 'views',
    type: EFormFieldType.CheckboxGroup,
    rules: { required: { value: true, message: 'nameErrorMessage' } },
    labelFormat: EFormCheckboxLabelFormat.Code,
    disableConditions: ['isActive'],
    values: [],
  },
  {
    field: 'isActive',
    type: EFormFieldType.Checkbox,
    values: [{ code: 'isActive', name: 'isActive' }],
    showFormLegend: false,
    labelFormat: EFormCheckboxLabelFormat.Name,
  },
];

export const RoleFormFields: IFormField[] = [
  {
    field: 'name',
    type: EFormFieldType.Text,
    rules: {
      required: { value: true, message: 'nameErrorMessage' },
      minLength: { value: 3, message: 'minLimitErrorMessage' },
      pattern: { value: RegExPatterns.AplhaNumericHyphenUnderscore, message: 'nameErrorMessage' },
    },
    disableConditions: ['edit'],
  },
  {
    field: 'description',
    type: EFormFieldType.Text,
    disableConditions: ['isActive'],
    rules: {
      required: { value: true, message: 'descriptionErrorMessage' },
      maxLength: { value: 100, message: 'maxLimitErrorMessage' },
    },
  },
  {
    field: 'actions',
    type: EFormFieldType.Checkbox,
    disableConditions: ['isActive'],
    labelFormat: EFormCheckboxLabelFormat.Name,
    showFormLegend: true,
    values: [
      { code: 'isDefault', name: 'isDefault' },
      { code: 'isActive', name: 'isActive' },
    ],
  },
];

export const MarketFormFields: IFormField[] = [
  {
    field: 'code',
    type: EFormFieldType.Text,
    rules: {
      required: { value: true, message: 'codeErrorMessage' },
      minLength: { value: 2, message: 'minLimitErrorMessage' },
      maxLength: { value: 2, message: 'maxLimitErrorMessage' },
      pattern: { value: RegExPatterns.MarketCode, message: 'codeErrorMessage' },
    },
    disableConditions: ['edit'],
  },
  {
    field: 'name',
    type: EFormFieldType.Text,
    rules: {
      required: { value: true, message: 'nameErrorMessage' },
      minLength: { value: 3, message: 'minLimitErrorMessage' },
      maxLength: { value: 20, message: 'maxLimitErrorMessage' },
      pattern: { value: RegExPatterns.AlphaNumeric, message: 'nameErrorMessage' },
    },
    disableConditions: ['isActive'],
  },
  {
    field: 'description',
    type: EFormFieldType.Text,
    disableConditions: ['isActive'],
  },
  {
    field: 'isActive',
    type: EFormFieldType.Checkbox,
    values: [{ code: 'isActive', name: 'isActive' }],
    showFormLegend: false,
    labelFormat: EFormCheckboxLabelFormat.Name,
  },
];

export const MarketAssocaiationFormFields: IFormField[] = [
  {
    field: 'productCode',
    type: EFormFieldType.Text,
    disableConditions: ['edit'],
  },
  {
    field: 'markets',
    type: EFormFieldType.CheckboxGroup,
    labelFormat: EFormCheckboxLabelFormat.CodeWithName,
    disableConditions: [],
    values: [],
  },
];

export const LanguageFormFields: IFormField[] = [
  {
    field: 'code',
    type: EFormFieldType.Text,
    rules: {
      required: { value: true, message: 'codeErrorMessage' },
      maxLength: { value: 5, message: 'maxLimitErrorMessage' },
      pattern: { value: RegExPatterns.LanguageCode, message: 'codeErrorMessage' },
    },
    disableConditions: ['edit'],
  },
  {
    field: 'name',
    type: EFormFieldType.Text,
    rules: {
      required: { value: true, message: 'nameErrorMessage' },
      minLength: { value: 3, message: 'minLimitErrorMessage' },
      pattern: { value: RegExPatterns.AplhaNumericHyphenUnderscore, message: 'nameErrorMessage' },
    },
    disableConditions: ['isActive'],
  },
  {
    field: 'description',
    type: EFormFieldType.Text,
    disableConditions: ['isActive'],
  },
  {
    field: 'isActive',
    type: EFormFieldType.Checkbox,
    values: [{ code: 'isActive', name: 'isActive' }],
    showFormLegend: false,
    labelFormat: EFormCheckboxLabelFormat.Name,
  },
];

export const LanguageAssociationFormFields: IFormField[] = [
  {
    field: 'productCode',
    type: EFormFieldType.Text,
    disableConditions: ['edit'],
  },
  {
    field: 'languages',
    type: EFormFieldType.CheckboxGroup,
    labelFormat: EFormCheckboxLabelFormat.CodeWithName,
    disableConditions: [],
  },
];

export const FeatureFlagFormFields: IFormField[] = [
  {
    field: 'flagName',
    type: EFormFieldType.Text,
    rules: {
      required: { value: true, message: 'nameErrorMessage' },
      minLength: { value: 3, message: 'minLimitErrorMessage' },
      maxLength: { value: 30, message: 'maxLimitErrorMessage' },
      pattern: { value: RegExPatterns.AlphaNumericSeperators, message: 'nameErrorMessage' },
    },
    disableConditions: ['edit'],
  },
  {
    field: 'description',
    type: EFormFieldType.Text,
    rules: {
      required: { value: true, message: 'descriptionErrorMessage' },
      minLength: { value: 1, message: 'minLimitErrorMessage' },
      maxLength: { value: 100, message: 'maxLimitErrorMessage' },
    },
    disableConditions: ['isActive'],
  },
  {
    field: 'applicationName',
    type: EFormFieldType.RadioGroup,
    disableConditions: ['isActive'],
    rules: {
      required: { value: true },
    },
  },
  {
    field: 'page',
    type: EFormFieldType.Text,
    rules: {
      required: { value: true, message: 'pageErrorMessage' },
      minLength: { value: 3, message: 'minLimitErrorMessage' },
      maxLength: { value: 50, message: 'maxLimitErrorMessage' },
      pattern: { value: RegExPatterns.AlphaNumericSeperators, message: 'pageErrorMessage' },
    },
    disableConditions: ['isActive'],
  },
  {
    field: 'actions',
    type: EFormFieldType.Checkbox,
    disableConditions: ['isActive'],
    labelFormat: EFormCheckboxLabelFormat.Name,
    showFormLegend: true,
    values: [
      { code: 'isVisible', name: 'isVisible' },
      { code: 'isEditable', name: 'isEditable' },
    ],
  },
];

export const MultiNumericFormFields: IFormField[] = [
  {
    field: 'code',
    type: EFormFieldType.Text,
    rules: {
      required: { value: true, message: 'codeErrorMessage' },
      minLength: { value: 4, message: 'minLimitErrorMessage' },
      maxLength: { value: 40, message: 'maxLimitErrorMessage' },
      pattern: { value: RegExPatterns.AplhaNumericHyphenUnderscore, message: 'codeErrorMessage' },
    },
    disableConditions: ['edit'],
  },
  {
    field: 'name',
    type: EFormFieldType.Text,
    rules: {
      required: { value: true, message: 'nameErrorMessage' },
      minLength: { value: 4, message: 'minLimitErrorMessage' },
      maxLength: { value: 40, message: 'maxLimitErrorMessage' },
    },
    disableConditions: ['isActive'],
  },
  {
    field: 'others',
    type: EFormFieldType.Checkbox,
    disableConditions: ['isActive'],
    showFormLegend: true,
    labelFormat: EFormCheckboxLabelFormat.Name,
    values: [{ code: 'isLocal', name: 'isLocal' }],
  },
];

export const RecordLimit = {
  default: 100,
  market: 300,
};

// Map actions to their default error messages
export const ERROR_MESSAGE_KEYS = {
  [EAction.Get]: 'messages.fail.getDetails',
  [EActionType.Refresh]: 'messages.fail.update',
  [EAction.Retrigger]: 'messages.fail.retriggeredFailed',
  [EAction.Download]: 'messages.fail.download',
  [EAction.CreateReport]: 'messages.fail.generate',
};

export const ProductReleaseActivities = [
  'Price',
  'SalesText',
  'Effectivity',
  'Rules',
  'Properties',
  'Images',
  'SubModelInfo',
];
